import React, { useEffect, useMemo, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { World, WorldAudit } from '../../types';
import { Empty, Loading, Note, PlayerHead, Section } from '../ui/primitives';
import { useNamesFor } from '../../hooks/useUsernames';
import { auditLabel, auditVerb, fmtDateTime, isVisibleAudit, timeAgo } from '../../utils/format';

/** Renders a list of audits in the design's activity style. Reused by Home and Admin. */
export const ActivityList: React.FC<{ audits: WorldAudit[]; showWorld?: boolean; limit?: number; emptyText?: string }> = ({
  audits,
  showWorld,
  limit,
  emptyText = "Nothing's happened yet",
}) => {
  const visible = useMemo(() => {
    const list = audits.filter(isVisibleAudit).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return limit ? list.slice(0, limit) : list;
  }, [audits, limit]);
  const ids = useMemo(() => visible.map((a) => a.playerId), [visible]);
  const nameOf = useNamesFor(ids);

  if (visible.length === 0) {
    return (
      <Empty icon="log" title={emptyText}>
        When players join or settings change, you'll see it here.
      </Empty>
    );
  }
  return (
    <>
      {visible.map((a) => (
        <div className="evt" key={a.id}>
          <PlayerHead uuid={a.playerId} size={24} title={a.playerId ? nameOf(a.playerId) : 'System'} />
          <span>
            {a.playerId ? (
              <>
                <b>{nameOf(a.playerId)}</b> {auditVerb(a.action)}
              </>
            ) : (
              <b>{auditLabel(a.action)}</b>
            )}
            {showWorld && a.worldName && <span className="wl"> · {a.worldName}</span>}
            {a.details && <small>{a.details}</small>}
          </span>
          <time title={fmtDateTime(a.timestamp)}>{timeAgo(a.timestamp)}</time>
        </div>
      ))}
    </>
  );
};

/** Per-world audit trail. */
const ActivityTab: React.FC<{ world: World; refreshKey: number }> = ({ world, refreshKey }) => {
  const [audits, setAudits] = useState<WorldAudit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Worlds.audits(world.id)
      .then((a) => {
        if (!cancelled) {
          setAudits(a);
          setError(null);
        }
      })
      .catch((e) => !cancelled && setError(errorMessage(e, 'Failed to load activity.')));
    return () => {
      cancelled = true;
    };
  }, [world.id, refreshKey]);

  return (
    <Section title="Activity" blurb="Every recorded change to this world, newest first.">
      {error && <Note tone="danger">{error}</Note>}
      {audits == null && !error ? <Loading /> : <ActivityList audits={audits || []} />}
    </Section>
  );
};

export default ActivityTab;
